import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, computed, effect, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { ProductLinkCardComponent } from 'src/app/catalog/components/product-link-card/product-link-card.component';
import { ImageMapItem } from 'src/app/product-galleries/models/product-gallery.model';

// Mirrors the server's crop anchor (@teamfoster/sdk/image ImageSharpConfig.parseAnchor) so we
// can reproduce which side of the original image the "cover" crop trims from.
const ANCHOR_OFFSET_RATIOS: Record<string, { offsetXRatio: number; offsetYRatio: number }> = {
  topleft: { offsetXRatio: 0, offsetYRatio: 0 },
  topcenter: { offsetXRatio: 0.5, offsetYRatio: 0 },
  topright: { offsetXRatio: 1, offsetYRatio: 0 },
  middleleft: { offsetXRatio: 0, offsetYRatio: 0.5 },
  middlecenter: { offsetXRatio: 0.5, offsetYRatio: 0.5 },
  middleright: { offsetXRatio: 1, offsetYRatio: 0.5 },
  bottomleft: { offsetXRatio: 0, offsetYRatio: 1 },
  bottomcenter: { offsetXRatio: 0.5, offsetYRatio: 1 },
  bottomright: { offsetXRatio: 1, offsetYRatio: 1 },
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

@Component({
  selector: 'app-media-point-map',
  imports: [ProductLinkCardComponent, OverlayModule],
  templateUrl: './media-point-map.component.html',
  styleUrl: './media-point-map.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaPointMapComponent {
  points = input<ImageMapItem[] | null>();
  connectedElement = input<HTMLElement | null>();
  hostIsConnnected = input<boolean>(false);
  showMinimalDotsFirst = input<boolean>(true);

  originalWidth = input<number | null>(null);
  originalHeight = input<number | null>(null);
  cropWidth = input<number | null>(null);
  cropHeight = input<number | null>(null);
  anchor = input<string | null>(null);

  elementEnter = output<void>();

  // `points()` coordinates are authored as percentages of the ORIGINAL, uncropped image.
  // The <img> we actually render is server-cropped to a fixed width/height box (see
  // MediaComponent's loaderParams), which trims off whatever doesn't fit that box's aspect
  // ratio. Without this remap, a dot's %-position would point at the wrong pixel as soon as
  // the crop box's aspect ratio differs from the original image's — this recomputes each dot's
  // position relative to the cropped box so it still lands on the spot it was placed on.
  mappedPoints = computed(() => {
    const points = this.points() ?? [];
    const origW = this.originalWidth();
    const origH = this.originalHeight();
    const cropW = this.cropWidth();
    const cropH = this.cropHeight();

    if (!origW || !origH || !cropW || !cropH) {
      return points;
    }

    // "Cover" crop: scale the original up until it fills the crop box, then trim the excess.
    const scale = Math.max(cropW / origW, cropH / origH);
    const visibleW = cropW / scale; // portion of the original image (in its own px) kept after crop
    const visibleH = cropH / scale;

    const { offsetXRatio, offsetYRatio } = ANCHOR_OFFSET_RATIOS[this.anchor() || 'middlecenter'] ?? ANCHOR_OFFSET_RATIOS['middlecenter'];
    const offsetX = (origW - visibleW) * offsetXRatio; // px trimmed off the left, per anchor
    const offsetY = (origH - visibleH) * offsetYRatio; // px trimmed off the top, per anchor

    return points.map(point => ({
      ...point,
      coordinates: {
        // original-image % -> original px -> shift by crop offset -> visible-region % -> clamp
        // (a point that fell inside the trimmed-away strip would otherwise land outside 0-100%)
        x: clamp((((point.coordinates.x / 100) * origW - offsetX) / visibleW) * 100, 0, 100),
        y: clamp((((point.coordinates.y / 100) * origH - offsetY) / visibleH) * 100, 0, 100),
      },
    }));
  });

  readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 12,
    },
    {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -12,
    },
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 12,
    },
    {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -12,
    },
  ];

  lastHoveredPointId: number | null = null;
  cd = inject(ChangeDetectorRef);
  private hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private router = inject(Router);
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.closeOverlay(), 150);
  }

  cancelClose() {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  constructor() {
    effect(onCleanup => {
      const targets = [this.connectedElement(), this.hostIsConnnected() ? this.hostElement.nativeElement : null].filter(
        (target): target is HTMLElement => !!target
      );

      if (!targets.length) return;

      for (const target of targets) target.classList.add('media-point-map-container');

      const onMouseEnter = () => {
        this.cancelClose();
        this.elementEnter.emit();
      };

      const onMouseLeave = () => this.scheduleClose();

      const onClick = (event: MouseEvent) => {
        if (this.isOverlayTarget(event.target)) return;
        this.closeOverlay();
      };

      for (const target of targets) {
        target.addEventListener('mouseenter', onMouseEnter);
        target.addEventListener('mouseleave', onMouseLeave as EventListener);
        target.addEventListener('click', onClick as EventListener);
      }

      onCleanup(() => {
        for (const target of targets) {
          target.classList.remove('media-point-map-container');
          target.removeEventListener('mouseenter', onMouseEnter);
          target.removeEventListener('mouseleave', onMouseLeave as EventListener);
          target.removeEventListener('click', onClick as EventListener);
        }
      });
    });
  }

  private isOverlayTarget(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest('.media-point-map-overlay');
  }

  openOverlay(event: Event, point: ImageMapItem, isClick = false) {
    event.stopPropagation();
    this.lastHoveredPointId = point.id;
    this.cd.markForCheck();

    //  if is non-touch device, go to link on click, otherwise open overlay
    if (isClick && !('ontouchstart' in window)) {
      this.router.navigate(['/', 'products', point.handle]);
    }
  }

  closeOverlay() {
    this.lastHoveredPointId = null;
    this.cd.markForCheck();
  }
}
